import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NgFor, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { ProfilePopupComponent } from './profile-popup/profile-popup.component';
import { ProfileGateComponent } from './profile-gate/profile-gate.component';
import { HorizontalPickerQuestionComponent } from './horizontal-picker-question/horizontal-picker-question.component';
import { MultipleChoiceQuestionComponent } from './multiple-choice-question/multiple-choice-question.component';
import { SingleChoiceQuestionComponent } from './single-choice-question/single-choice-question.component';
import { VerticalPickerQuestionComponent } from './vertical-picker-question/vertical-picker-question.component';
import { DateQuestionComponent } from './date-question/date-question.component';
import { ProfileAnswer, ProfileQuestion } from './profile-question.model';

/**
 * Chat profile onboarding: the "Complete your health profile" gate, then one popup per
 * missing question. Answers are emitted one by one so progress survives closing it midway.
 */
@Component({
  selector: 'app-profile-onboarding',
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    ProfilePopupComponent,
    ProfileGateComponent,
    HorizontalPickerQuestionComponent,
    MultipleChoiceQuestionComponent,
    SingleChoiceQuestionComponent,
    VerticalPickerQuestionComponent,
    DateQuestionComponent,
  ],
  template: `
    <app-profile-popup [open]="open" (dismissed)="dismissed.emit()">
      <app-profile-gate *ngIf="step < 0" (start)="step = 0"></app-profile-gate>

      <!-- A one-item loop keyed by id gives every question a fresh component -->
      <ng-container *ngFor="let question of currentQuestion; trackBy: trackById">
        <!-- As in the app: a Skip box in the top right corner -->
        <div *ngIf="question.skippable" class="-mt-4 mb-4 flex justify-end md:-mr-6 md:-mt-8">
          <button
            type="button"
            class="bg-[#DAD6FB] px-5 py-2.5 text-[17px] leading-[22px]"
            (click)="onAnswer(question, null)"
          >Skip</button>
        </div>
        <ng-container [ngSwitch]="question.type">
          <app-horizontal-picker-question *ngSwitchCase="'horizontal-picker'" [question]="$any(question)" (answer)="onAnswer(question, $event)"></app-horizontal-picker-question>
          <app-multiple-choice-question *ngSwitchCase="'multiple-choice'" [question]="$any(question)" (answer)="onAnswer(question, $event)"></app-multiple-choice-question>
          <app-single-choice-question *ngSwitchCase="'single-choice'" [question]="$any(question)" (answer)="onAnswer(question, $event)"></app-single-choice-question>
          <app-vertical-picker-question *ngSwitchCase="'vertical-picker'" [question]="$any(question)" (answer)="onAnswer(question, $event)"></app-vertical-picker-question>
          <app-date-question *ngSwitchCase="'date'" [question]="$any(question)" (answer)="onAnswer(question, $event)"></app-date-question>
        </ng-container>
      </ng-container>
    </app-profile-popup>
  `
})
export class ProfileOnboardingComponent implements OnChanges {
  @Input() open = false;
  @Input() questions: ProfileQuestion[] = [];
  /**
   * Called after each answer; a returned list replaces `questions`, and asking continues
   * after the answered question's index. Lets an answer (e.g. the track) decide what follows.
   */
  @Input() questionsAfter?: (answer: ProfileAnswer) => ProfileQuestion[] | undefined;
  @Output() answered = new EventEmitter<ProfileAnswer>();
  /** All questions answered. */
  @Output() completed = new EventEmitter<void>();
  @Output() dismissed = new EventEmitter<void>();

  /** -1 shows the gate; 0.. indexes into `questions`. */
  step = -1;
  /** Answers given since the popup opened, for `showIf`. */
  private answers = new Map<string, ProfileAnswer['value']>();

  ngOnChanges(changes: SimpleChanges) {
    if (changes['open'] && this.open) {
      this.step = -1;
      this.answers.clear();
    }
  }

  get currentQuestion(): ProfileQuestion[] {
    const question = this.questions[this.step];
    return question ? [question] : [];
  }

  trackById(_: number, question: ProfileQuestion): string {
    return question.id;
  }

  onAnswer(question: ProfileQuestion, value: ProfileAnswer['value']) {
    const answer: ProfileAnswer = { questionId: question.id, value };
    this.answered.emit(answer);
    this.answers.set(question.id, value);
    this.questions = this.questionsAfter?.(answer) ?? this.questions;
    do {
      this.step++;
    } while (this.step < this.questions.length && !this.isAsked(this.questions[this.step]));
    if (this.step >= this.questions.length) {
      this.completed.emit();
    }
  }

  /**
   * Follow-ups are skipped when the answer they depend on, given here, rules them out. When it was
   * answered earlier instead, the service only passes in the follow-ups that apply.
   */
  private isAsked(question: ProfileQuestion): boolean {
    if (!question.showIf || !this.answers.has(question.showIf.questionId)) return true;
    const answer = this.answers.get(question.showIf.questionId);
    return typeof answer === 'string' && question.showIf.values.includes(answer);
  }
}
